import type {
  AutomationBindingResult,
  AutomationScheduleResult,
  ChannelDetail,
  QrAvailability,
  QrMigrationResult,
  WhatsAppCloudDisconnectResult,
  ChannelHealth,
  ChannelSyncResult,
  ChannelDoctorReport,
  ChannelDoctorFixResult,
  ChannelSelfCheckResult,
  QrWhatsAppProfile,
  WhatsAppBusinessProfile,
  ChannelSummary,
  QrChannelProvisionResult,
  InstagramChannelStatus,
  InstagramShopStatus,
  InstagramShopTestResult,
  QrChannelSettingsView,
  QrGroupsSetting,
  QrChannelStatusView,
  QrRiskAcknowledgementResult,
  QrRiskAcknowledgementView,
  WhatsAppCloudSignupConfig,
  WhatsAppCloudSignupResult,
} from "../types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type {
  InstagramConfigStatus,
  InstagramPageChoice,
  TikTokConfigStatus,
  InstagramIceBreaker,
  InstagramSyncResult,
  InstagramKeyword,
  InstagramPersistentMenu,
  InstagramMediaPage,
  InstagramCommentRule,
} from "../client-types";

export type ChannelsApiMethods = Pick<
  ApiClient,
  | "getWhatsAppCloudSignupConfig"
  | "exchangeWhatsAppCloudSignupCode"
  | "acceptQrRiskDisclosure"
  | "listQrRiskAcknowledgements"
  | "provisionQrChannel"
  | "getQrChannelStatus"
  | "rescanQrChannel"
  | "deleteChannel"
  | "setDefaultChannel"
  | "renameChannel"
  | "syncChannel"
  | "disconnectQrChannel"
  | "getQrGroups"
  | "setQrGroups"
  | "getQrSettings"
  | "setQrSettings"
  | "reimportQrHistory"
  | "setChannelAutomationBinding"
  | "saveChannelAiSettings"
  | "getChannelAutomationSchedule"
  | "setChannelAutomationSchedule"
  | "listChannels"
  | "selfCheckChannel"
  | "getChannelDoctor"
  | "fixChannelDoctor"
  | "getChannelHealth"
  | "manualConnectWhatsAppCloud"
  | "getInstagramConfigStatus"
  | "getTikTokConfigStatus"
  | "startTikTokConnect"
  | "startInstagramConnect"
  | "listInstagramPages"
  | "enableInstagramPage"
  | "disconnectInstagramChannel"
  | "getInstagramStatus"
  | "getInstagramShopStatus"
  | "sendInstagramShopTest"
  | "resubscribeInstagram"
  | "listInstagramIceBreakers"
  | "saveInstagramIceBreakers"
  | "getInstagramPersistentMenu"
  | "saveInstagramPersistentMenu"
  | "listInstagramMedia"
  | "listInstagramCommentRules"
  | "saveInstagramCommentRule"
  | "deleteInstagramCommentRule"
  | "listInstagramKeywords"
  | "addInstagramKeyword"
  | "removeInstagramKeyword"
  | "getChannel"
  | "updateChannelSettings"
  | "getWhatsAppBusinessProfile"
  | "updateWhatsAppBusinessProfile"
  | "getQrWhatsAppProfile"
  | "updateQrWhatsAppProfile"
  | "disconnectWhatsAppCloud"
  | "reconnectWhatsAppCloud"
  | "getQrAvailability"
  | "migrateQrToCloud"
>;

export function createChannelsApi({ request, ws }: HttpTransport): ChannelsApiMethods {
  const igBase = (id: string, channelId: string): string =>
    `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram`;
  return {
    getWhatsAppCloudSignupConfig: (id) =>
      request<WhatsAppCloudSignupConfig>(
        "GET",
        `${ws(id)}/channels/whatsapp-cloud/embedded-signup/config`,
      ),
    exchangeWhatsAppCloudSignupCode: (id, input) =>
      request<WhatsAppCloudSignupResult>(
        "POST",
        `${ws(id)}/channels/whatsapp-cloud/embedded-signup/callback`,
        input,
      ),
    acceptQrRiskDisclosure: (id) =>
      request<QrRiskAcknowledgementResult>("POST", `${ws(id)}/compliance/qr-risk-acknowledgement`),
    async listQrRiskAcknowledgements(id) {
      return (
        await request<{ acknowledgements: QrRiskAcknowledgementView[] }>(
          "GET",
          `${ws(id)}/compliance/qr-risk-acknowledgements`,
        )
      ).acknowledgements;
    },
    provisionQrChannel: (id) => request<QrChannelProvisionResult>("POST", `${ws(id)}/channels/qr`),
    getQrChannelStatus: (id, channelId) =>
      request<QrChannelStatusView>("GET", `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}`),
    rescanQrChannel: (id, channelId) =>
      request<QrChannelStatusView>(
        "POST",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/rescan`,
      ),
    async deleteChannel(id, channelId, confirmation) {
      await request("DELETE", `${ws(id)}/channels/${encodeURIComponent(channelId)}`, {
        confirmation,
      });
    },
    async renameChannel(id, channelId, displayName) {
      await request("PATCH", `${ws(id)}/channels/${encodeURIComponent(channelId)}`, {
        displayName,
      });
    },
    syncChannel: (id, channelId) =>
      request<ChannelSyncResult>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/sync`,
      ),
    async setDefaultChannel(id, channelId) {
      await request("POST", `${ws(id)}/channels/${encodeURIComponent(channelId)}/set-default`);
    },
    async disconnectQrChannel(id, channelId) {
      await request("POST", `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/disconnect`);
    },
    getQrGroups: (id, channelId) =>
      request<QrGroupsSetting>(
        "GET",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/groups`,
      ),
    setQrGroups: (id, channelId, receiveGroups) =>
      request<QrGroupsSetting>(
        "PUT",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/groups`,
        {
          receiveGroups,
        },
      ),
    getQrSettings: (id, channelId) =>
      request<QrChannelSettingsView>(
        "GET",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/settings`,
      ),
    setQrSettings: (id, channelId, patch) =>
      request<QrChannelSettingsView>(
        "PUT",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/settings`,
        patch,
      ),
    reimportQrHistory: (id, channelId) =>
      request<{ status: "done"; chats: number; newMessages: number }>(
        "POST",
        `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/history/reimport`,
      ),
    setChannelAutomationBinding: (id, channelId, binding) =>
      request<AutomationBindingResult>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation-binding`,
        { binding },
      ),
    async saveChannelAiSettings(id, channelId, settings) {
      await request(
        "PATCH",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation-binding`,
        settings,
      );
    },
    getChannelAutomationSchedule: (id, channelId) =>
      request<AutomationScheduleResult>(
        "GET",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation-schedule`,
      ),
    setChannelAutomationSchedule: (id, channelId, schedule) =>
      request<AutomationScheduleResult>(
        "PATCH",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation-schedule`,
        schedule,
      ),
    async listChannels(id) {
      return (await request<{ channels: ChannelSummary[] }>("GET", `${ws(id)}/channels`)).channels;
    },
    selfCheckChannel: (id, channelId) =>
      request<ChannelSelfCheckResult>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/self-check`,
      ),
    getChannelDoctor: (id) =>
      request<{ reports: ChannelDoctorReport[] }>("GET", `${ws(id)}/channels/doctor`),
    fixChannelDoctor: (id, channelId, action) =>
      request<ChannelDoctorFixResult>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/doctor/fix`,
        { action },
      ),
    getChannelHealth: (id, channelId) =>
      request<ChannelHealth>("GET", `${ws(id)}/channels/${encodeURIComponent(channelId)}/health`),
    manualConnectWhatsAppCloud: (id, input) =>
      request<WhatsAppCloudSignupResult>(
        "POST",
        `${ws(id)}/channels/whatsapp-cloud/manual-connect`,
        input,
      ),
    getTikTokConfigStatus: (id) =>
      request<TikTokConfigStatus>("GET", `${ws(id)}/channels/tiktok/config-status`),
    startTikTokConnect: (id) =>
      request<{ url: string }>("POST", `${ws(id)}/channels/tiktok/connect`),
    getInstagramConfigStatus: (id) =>
      request<InstagramConfigStatus>("GET", `${ws(id)}/channels/instagram/config-status`),
    startInstagramConnect: (id) =>
      request<{ url: string }>("POST", `${ws(id)}/channels/instagram/connect`),
    async listInstagramPages(id, selection) {
      return (
        await request<{ pages: InstagramPageChoice[] }>(
          "GET",
          `${ws(id)}/channels/instagram/pages?selection=${encodeURIComponent(selection)}`,
        )
      ).pages;
    },
    enableInstagramPage: (id, pageId, selection) =>
      request<{ channelId: string; username: string; webhooksActive: boolean }>(
        "POST",
        `${ws(id)}/channels/instagram/pages/${encodeURIComponent(pageId)}/enable`,
        { selection },
      ),
    getInstagramStatus: (id, channelId) =>
      request<InstagramChannelStatus>(
        "GET",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/status`,
      ),
    getInstagramShopStatus: (id, channelId) =>
      request<InstagramShopStatus>(
        "GET",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/shop`,
      ),
    sendInstagramShopTest: (id, channelId, recipientId) =>
      request<InstagramShopTestResult>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/shop/test`,
        { recipientId },
      ),
    resubscribeInstagram: (id, channelId) =>
      request<{ webhooksActive: boolean }>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/resubscribe`,
      ),
    async disconnectInstagramChannel(id, channelId) {
      await request<unknown>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/disconnect`,
      );
    },
    async listInstagramIceBreakers(id, channelId) {
      return (
        await request<{ items: InstagramIceBreaker[] }>(
          "GET",
          `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/ice-breakers`,
        )
      ).items;
    },
    async saveInstagramIceBreakers(id, channelId, items) {
      return (
        await request<{ results: InstagramSyncResult[] }>(
          "POST",
          `${ws(id)}/channels/${encodeURIComponent(channelId)}/instagram/ice-breakers`,
          { items },
        )
      ).results;
    },
    getInstagramPersistentMenu: (id, channelId) =>
      request<InstagramPersistentMenu>("GET", `${igBase(id, channelId)}/persistent-menu`),
    saveInstagramPersistentMenu: (id, channelId, input) =>
      request("POST", `${igBase(id, channelId)}/persistent-menu`, input),
    listInstagramMedia(id, channelId, page = {}) {
      const q = new URLSearchParams();
      if (page.after) q.set("after", page.after);
      if (page.limit) q.set("limit", String(page.limit));
      const qs = q.toString();
      return request<InstagramMediaPage>(
        "GET",
        `${igBase(id, channelId)}/media${qs ? `?${qs}` : ""}`,
      );
    },
    async listInstagramCommentRules(id, channelId) {
      return (
        await request<{ items: InstagramCommentRule[] }>(
          "GET",
          `${igBase(id, channelId)}/comment-rules`,
        )
      ).items;
    },
    saveInstagramCommentRule: (id, channelId, rule) =>
      request<InstagramCommentRule>("PUT", `${igBase(id, channelId)}/comment-rules`, rule),
    async deleteInstagramCommentRule(id, channelId, scope, mediaId) {
      const q = new URLSearchParams({ scope });
      if (mediaId) q.set("mediaId", mediaId);
      await request("DELETE", `${igBase(id, channelId)}/comment-rules?${q.toString()}`);
    },
    async listInstagramKeywords(id) {
      return (
        await request<{ items: InstagramKeyword[] }>(
          "GET",
          `${ws(id)}/channels/instagram/moderation/keywords`,
        )
      ).items;
    },
    addInstagramKeyword: (id, keyword) =>
      request<InstagramKeyword>("POST", `${ws(id)}/channels/instagram/moderation/keywords`, {
        keyword,
      }),
    async removeInstagramKeyword(id, keywordId) {
      await request<unknown>(
        "DELETE",
        `${ws(id)}/channels/instagram/moderation/keywords/${encodeURIComponent(keywordId)}`,
      );
    },
    async getChannel(id, channelId) {
      return (
        await request<{ channel: ChannelDetail }>(
          "GET",
          `${ws(id)}/channels/${encodeURIComponent(channelId)}`,
        )
      ).channel;
    },
    async updateChannelSettings(id, channelId, input) {
      return (
        await request<{ channel: ChannelDetail }>(
          "PATCH",
          `${ws(id)}/channels/${encodeURIComponent(channelId)}/settings`,
          input,
        )
      ).channel;
    },
    async getWhatsAppBusinessProfile(id, channelId) {
      return (
        await request<{ profile: WhatsAppBusinessProfile }>(
          "GET",
          `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/profile`,
        )
      ).profile;
    },
    async getQrWhatsAppProfile(id, channelId) {
      return (
        await request<{ profile: QrWhatsAppProfile }>(
          "GET",
          `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/profile`,
        )
      ).profile;
    },
    async updateQrWhatsAppProfile(id, channelId, input) {
      return (
        await request<{ profile: QrWhatsAppProfile }>(
          "PATCH",
          `${ws(id)}/channels/qr/${encodeURIComponent(channelId)}/profile`,
          input,
        )
      ).profile;
    },
    async updateWhatsAppBusinessProfile(id, channelId, input) {
      return (
        await request<{ profile: WhatsAppBusinessProfile }>(
          "PATCH",
          `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/profile`,
          input,
        )
      ).profile;
    },
    disconnectWhatsAppCloud: (id, channelId) =>
      request<WhatsAppCloudDisconnectResult>(
        "POST",
        `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/disconnect`,
      ),
    reconnectWhatsAppCloud: (id, channelId, input) =>
      request<WhatsAppCloudSignupResult>(
        "POST",
        `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/reconnect`,
        input,
      ),
    getQrAvailability: (id) => request<QrAvailability>("GET", `${ws(id)}/channels/qr/availability`),
    migrateQrToCloud: (id, wahaChannelId, cloudChannelId) =>
      request<QrMigrationResult>(
        "POST",
        `${ws(id)}/channels/whatsapp-waha/${encodeURIComponent(wahaChannelId)}/migrate-to-cloud`,
        { cloudChannelId },
      ),
  };
}
